import { useConfirm } from "../context/ConfirmContext";
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { getJob, postToLinkedIn, activateJob, deactivateJob, downloadFormattedCV, downloadFormattedDocx, updateStatus, updateJob, regenerateCV, deleteJob, getBranding, uploadCVManual, getTalentPool, generateInterviewQuestions, addToJobPipeline, generateOutreachEmail, getLinkedInUserStatus, getLinkedInAuthUrl } from "../api";
import { useToast } from "../context/ToastContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useBlockingDownload } from "../hooks/useBlockingDownload";
import { formatDate } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { SUPPORTED_JOB_LANGUAGES, localeForLanguage } from "../utils/language";
import JobScreeningQuestions from "../components/JobScreeningQuestions";
import ScreeningFilterBar from "../components/ScreeningFilterBar";

const REC_COLORS: Record<string, string> = {
  "Strong Yes": "#16a34a", "Yes": "#2563eb", "Maybe": "#d97706", "No": "#dc2626"
};

export default function JobDetailPage() {
  const tz = useTenantTimezone();
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const confirm = useConfirm();
  const { toast } = useToast();
  const { downloading, download } = useBlockingDownload();
  const [linkedInMsg, setLinkedInMsg] = useState("");
  const [talentPool, setTalentPool]     = useState<any | null>(null);
  const [tpLoading, setTpLoading]       = useState(false);
  const [addingId, setAddingId]         = useState<number | null>(null);
  const [addedIds, setAddedIds]         = useState<Set<number>>(new Set());
  const [iqCandidate, setIqCandidate]   = useState<any | null>(null);
  const [iqLoading, setIqLoading]       = useState(false);
  const [iqResult, setIqResult]         = useState<any | null>(null);
  const [outreachCandidate, setOutreachCandidate] = useState<any | null>(null);
  const [outreachLoading, setOutreachLoading]     = useState(false);
  const [outreachResult, setOutreachResult]       = useState<any | null>(null);
  const [regenMsg, setRegenMsg] = useState<Record<number, string>>({});
  const [uploadMsg, setUploadMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number; failed: number } | null>(null);
  const cvInputRef = React.useRef<HTMLInputElement>(null);

  async function handleCVUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []) as File[];
    if (!files.length) return;
    setUploading(true);
    setUploadMsg("");
    setUploadProgress({ done: 0, total: files.length, failed: 0 });

    // Fire all uploads in parallel — backend queues each into BackgroundTasks
    const results = await Promise.allSettled(
      files.map(file => uploadCVManual(file, Number(id)))
    );

    const failed = results.filter(r => r.status === "rejected").length;
    const succeeded = results.length - failed;
    setUploadProgress(null);
    qc.invalidateQueries({ queryKey: ["job", id] });

    if (failed === 0) {
      setUploadMsg(`✓ ${succeeded} CV${succeeded !== 1 ? "s" : ""} queued for AI scoring — refresh in ~30s`);
    } else if (succeeded === 0) {
      setUploadMsg(`✗ All ${failed} uploads failed — check file types (PDF/DOCX only)`);
    } else {
      setUploadMsg(`✓ ${succeeded} queued · ${failed} failed`);
    }
    setTimeout(() => setUploadMsg(""), 10000);
    setUploading(false);
    if (cvInputRef.current) cvInputRef.current.value = "";
  }
  const [visibleAppIds, setVisibleAppIds] = useState<Set<number> | null>(null);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editSummary, setEditSummary] = useState("");
  const [editResponsibilities, setEditResponsibilities] = useState("");
  const [editSkills, setEditSkills] = useState("");
  const [editNiceToHave, setEditNiceToHave] = useState("");
  const [editExperience, setEditExperience] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editType, setEditType] = useState("");
  const [editBudget, setEditBudget] = useState("");
  const [editLanguage, setEditLanguage] = useState("English");
  const [editLinkedIn, setEditLinkedIn] = useState("");
  const [editKnockouts, setEditKnockouts] = useState<{ type: string; value: string }[]>([]);

  const { data: job, isLoading } = useQuery({
    queryKey: ["job", id],
    queryFn: () => getJob(Number(id))
  });
  const { data: branding } = useQuery({ queryKey: ["branding"], queryFn: getBranding, staleTime: 5 * 60 * 1000 });
  const jobsEmail: string = branding?.jobs_email || "";
  const { data: liStatus } = useQuery({ queryKey: ["li-user-status"], queryFn: getLinkedInUserStatus, retry: false });
  const { data: liAuthData } = useQuery({ queryKey: ["li-auth"], queryFn: getLinkedInAuthUrl, retry: false, enabled: !(liStatus as any)?.connected });
  const liConnected: boolean = (liStatus as any)?.connected ?? false;

  const linkedInMutation = useMutation({
    mutationFn: () => postToLinkedIn(Number(id)),
    onSuccess: (data) => {
      setLinkedInMsg(`✓ Posted to: ${data.posted_to.join(", ")}`);
      qc.invalidateQueries({ queryKey: ["job", id] });
    },
    onError: (e: any) => setLinkedInMsg(`❌ ${e?.response?.data?.detail || "LinkedIn posting failed."}`)
  });
  const activateMutation = useMutation({
    mutationFn: () => activateJob(Number(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job", id] })
  });

  const deactivateMutation = useMutation({
    mutationFn: () => deactivateJob(Number(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job", id] })
  });

  const editMutation = useMutation({
    mutationFn: (fd: FormData) => updateJob(Number(id), fd),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", id] });
      setEditing(false);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteJob(Number(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      nav("/");
    }
  });

  const statusMutation = useMutation({
    mutationFn: ({ appId, status }: { appId: number; status: string }) => updateStatus(appId, status),
    onMutate: async ({ appId, status }) => {
      await qc.cancelQueries({ queryKey: ["job", id] });
      const previousJob = qc.getQueryData(["job", id]);
      qc.setQueryData(["job", id], (old: any) => old && {
        ...old,
        applications: (old.applications || []).map((a: any) => a.id === appId ? { ...a, status } : a),
      });
      return { previousJob };
    },
    onError: (e: any, _vars, context) => {
      if (context?.previousJob) qc.setQueryData(["job", id], context.previousJob);
      toast(e?.response?.data?.detail || "Couldn't move candidate — try again", "error");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["job", id] }),
  });

  const handleDelete = () => {
    confirm(`Delete "${job?.title}"? This will also remove all ${job?.applications?.length || 0} application(s). This cannot be undone.`, { confirmLabel: "Delete Job" }).then(ok => {
      if (ok) deleteMutation.mutate();
    });
  };

  const startEdit = () => {
    const jd = job?.jd || {};
    setEditTitle(job.title || "");
    setEditSummary(jd.summary || "");
    setEditResponsibilities((jd.responsibilities || []).join("\n"));
    setEditSkills((jd.required_skills || []).join("\n"));
    setEditNiceToHave((jd.nice_to_have || []).join("\n"));
    setEditExperience(jd.experience_years || "");
    setEditLocation(jd.location || "");
    setEditType(jd.employment_type || "Contract");
    setEditBudget(jd.budget || "");
    setEditLanguage(job.language || "English");
    setEditLinkedIn(job.linkedin_post_text || "");
    setEditKnockouts(job.knockout_criteria || jd.knockout_criteria || []);
    setEditing(true);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("title", editTitle);
    fd.append("summary", editSummary);
    fd.append("responsibilities", editResponsibilities);
    fd.append("required_skills", editSkills);
    fd.append("nice_to_have", editNiceToHave);
    fd.append("experience_years", editExperience);
    fd.append("location", editLocation);
    fd.append("employment_type", editType);
    fd.append("budget", editBudget);
    fd.append("linkedin_post", editLinkedIn);
    fd.append("language", editLanguage.trim() || "English");
    fd.append("knockout_criteria", JSON.stringify(editKnockouts.filter(k => k.value.trim())));
    editMutation.mutate(fd);
  };

  const handleDownloadCV = async (candidateId: number, applicationId?: number | null) => {
    await download(
      () => downloadFormattedCV(candidateId, applicationId),
      "CV not ready — click Regenerate CV to rebuild it",
    );
  };

  const handleDownloadDocx = async (candidateId: number, applicationId?: number | null) => {
    await download(
      () => downloadFormattedDocx(candidateId, applicationId),
      "Word CV not ready — click Regenerate CV to rebuild it",
    );
  };

  const handleRegenerateCV = async (candidateId: number, applicationId?: number | null) => {
    try {
      await regenerateCV(candidateId, applicationId);
      toast("CV queued for regeneration — refresh in ~30s", "success");
      qc.invalidateQueries({ queryKey: ["job", id] });
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Regenerate CV failed", "error");
    }
  };

  async function handleTalentPool() {
    if (talentPool) return; // already have results — click Clear to re-run
    setTpLoading(true);
    try { setTalentPool(await getTalentPool(Number(id))); }
    catch (e: any) { toast(e?.response?.data?.detail || "Talent pool search failed", "error"); }
    finally { setTpLoading(false); }
  }

  async function handleGenerateIQ(app: any) {
    setIqCandidate(app); setIqResult(null); setIqLoading(true);
    try { setIqResult(await generateInterviewQuestions(app.candidate_id, Number(id))); }
    catch (e: any) { toast(e?.response?.data?.detail || "Failed to generate questions", "error"); setIqCandidate(null); }
    finally { setIqLoading(false); }
  }

  async function handleAddToPipeline(match: any) {
    setAddingId(match.candidate_id);
    try {
      await addToJobPipeline(Number(id), match.candidate_id);
      setAddedIds(s => new Set(s).add(match.candidate_id));
      qc.invalidateQueries({ queryKey: ["job", id] });
      toast(`${match.candidate_name} added to pipeline`, "success");
    } catch (e: any) {
      toast(e?.response?.data?.detail || "Failed to add candidate", "error");
    } finally { setAddingId(null); }
  }

  async function handleOutreachEmail(match: any) {
    setOutreachCandidate(match); setOutreachResult(null); setOutreachLoading(true);
    try { setOutreachResult(await generateOutreachEmail(match.candidate_id, Number(id))); }
    catch (e: any) { toast(e?.response?.data?.detail || "Failed to generate email", "error"); setOutreachCandidate(null); }
    finally { setOutreachLoading(false); }
  }

  if (isLoading) return <p style={{ color: "#64748b" }}>Loading...</p>;
  if (!job) return <p style={{ color: "#dc2626" }}>Job not found</p>;

  const jd = job.jd || {};
  const pipelineApplications = visibleAppIds
    ? (job.applications || []).filter((a: any) => visibleAppIds.has(a.id))
    : (job.applications || []);

  if (editing) {
    return (
      <div style={{ maxWidth: 700 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 24 }}>
          <button onClick={() => setEditing(false)} style={btnStyle("#e2e8f0", "#334155")}>← Cancel</button>
          <h2 style={{ margin: 0, color: "#1e3a8a" }}>Edit Job</h2>
        </div>
        <form onSubmit={handleEditSubmit} style={cardStyle}>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Job Title *</label>
            <input required value={editTitle} onChange={e => setEditTitle(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Summary</label>
            <textarea value={editSummary} onChange={e => setEditSummary(e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Required Skills (one per line)</label>
              <textarea value={editSkills} onChange={e => setEditSkills(e.target.value)} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div>
              <label style={labelStyle}>Responsibilities (one per line)</label>
              <textarea value={editResponsibilities} onChange={e => setEditResponsibilities(e.target.value)} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
          </div>
          <div className="grid-3col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Experience</label>
              <input value={editExperience} onChange={e => setEditExperience(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Budget</label>
              <input value={editBudget} onChange={e => setEditBudget(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Type</label>
              <select value={editType} onChange={e => setEditType(e.target.value)} style={inputStyle}>
                <option>Contract</option>
                <option>Full-time</option>
                <option>Part-time</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Location</label>
            <input value={editLocation} onChange={e => setEditLocation(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Posting Language</label>
            <select value={editLanguage} onChange={e => setEditLanguage(e.target.value)} style={inputStyle}>
              {/* A job saved with a language outside the supported list (legacy free-text
                  entry) keeps its value as an extra option instead of being silently
                  reset to the first supported language. */}
              {!SUPPORTED_JOB_LANGUAGES.includes(editLanguage) && (
                <option value={editLanguage}>{editLanguage}</option>
              )}
              {SUPPORTED_JOB_LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              Knockout Criteria <span style={{ color: "#94a3b8", fontWeight: 400 }}>(auto-reject if not met)</span>
            </label>
            {editKnockouts.map((k, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                <select value={k.type} onChange={e => setEditKnockouts(ks => ks.map((x, j) => j === i ? { ...x, type: e.target.value } : x))}
                  style={{ ...inputStyle, width: 180, marginBottom: 0 }}>
                  <option value="min_experience">Min experience</option>
                  <option value="required_degree">Required degree</option>
                  <option value="location">Must be in location</option>
                </select>
                <input value={k.value} placeholder={k.type === "min_experience" ? "e.g. 5 years" : k.type === "required_degree" ? "e.g. Bachelor" : "e.g. Bangkok"}
                  onChange={e => setEditKnockouts(ks => ks.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                  style={{ ...inputStyle, flex: 1, marginBottom: 0 }} />
                <button type="button" onClick={() => setEditKnockouts(ks => ks.filter((_, j) => j !== i))}
                  style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 18, padding: "0 4px" }}>×</button>
              </div>
            ))}
            <button type="button" onClick={() => setEditKnockouts(ks => [...ks, { type: "min_experience", value: "" }])}
              style={{ fontSize: 13, color: "#1e3a8a", background: "none", border: "1px dashed #94a3b8", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}>
              + Add criterion
            </button>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>LinkedIn Post Text</label>
            <textarea value={editLinkedIn} onChange={e => setEditLinkedIn(e.target.value)} rows={6} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          <button type="submit" disabled={editMutation.isPending || !editTitle}
            style={{ ...btnStyle("linear-gradient(135deg,#1e3a8a,#00c9a7)", "white"), width: "100%", padding: "12px 0", opacity: editMutation.isPending ? 0.6 : 1 }}>
            {editMutation.isPending ? "Saving..." : "Save Changes"}
          </button>
          {editMutation.isError && <div style={{ color: "#dc2626", fontSize: 13, marginTop: 10 }}>Failed to save. Please try again.</div>}
        </form>
      </div>
    );
  }

  const CAT_COLORS: Record<string, string> = {
    "Technical/Skills": "#1e3a8a", "Behavioral": "#7c3aed",
    "Role-Specific": "#0f766e", "Gap-Probing": "#b45309",
  };

  return (
    <div>
      <LoadingOverlay active={
        activateMutation.isPending || deactivateMutation.isPending || editMutation.isPending ||
        deleteMutation.isPending || statusMutation.isPending || linkedInMutation.isPending ||
        downloading
      } />
      {/* ── Outreach Email Modal ── */}
      {outreachCandidate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", maxWidth: 600, width: "100%", maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0, color: "#1e3a8a", fontSize: 17 }}>Outreach Email Draft</h3>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>{outreachCandidate.candidate_name} · {outreachCandidate.email || ""}</div>
              </div>
              <button onClick={() => { setOutreachCandidate(null); setOutreachResult(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            {outreachLoading && <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b" }}><div style={{ fontSize: 28, marginBottom: 12 }}>🤖</div>Drafting email…</div>}
            {outreachResult && (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Subject</label>
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#0f172a", fontWeight: 500 }}>{outreachResult.subject}</div>
                </div>
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Body</label>
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px", fontSize: 13, color: "#334155", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{outreachResult.body}</div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => { navigator.clipboard.writeText(`Subject: ${outreachResult.subject}\n\n${outreachResult.body}`).then(() => toast("Copied!", "success")); }}
                    style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#334155" }}>
                    📋 Copy Email
                  </button>
                  {outreachResult.email && (
                    <a href={`mailto:${outreachResult.email}?subject=${encodeURIComponent(outreachResult.subject)}&body=${encodeURIComponent(outreachResult.body)}`}
                      style={{ background: "linear-gradient(135deg,#1e3a8a,#00c9a7)", color: "white", border: "none", borderRadius: 7, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                      ✉ Open in Mail
                    </a>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Interview Questions Modal ── */}
      {iqCandidate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "white", borderRadius: 14, padding: 28, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", maxWidth: 700, width: "100%", maxHeight: "88vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0, color: "#1e3a8a", fontSize: 17 }}>AI Interview Questions</h3>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>{iqCandidate.candidate_name} · {job.title}</div>
              </div>
              <button onClick={() => { setIqCandidate(null); setIqResult(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            {iqLoading && <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b" }}><div style={{ fontSize: 28, marginBottom: 12 }}>🤖</div>Generating questions…</div>}
            {iqResult && (
              <>
                {["Technical/Skills", "Behavioral", "Role-Specific", "Gap-Probing"].map(cat => {
                  const qs = (iqResult.questions || []).filter((q: any) => q.category === cat);
                  if (!qs.length) return null;
                  const color = CAT_COLORS[cat] || "#475569";
                  return (
                    <div key={cat} style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block" }} />{cat}
                      </div>
                      {qs.map((q: any, i: number) => (
                        <div key={i} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
                          <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 500, lineHeight: 1.5 }}>{q.question}</div>
                          {q.purpose && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 5 }}>Purpose: {q.purpose}</div>}
                        </div>
                      ))}
                    </div>
                  );
                })}
                <button onClick={() => { const t = (iqResult.questions||[]).map((q:any)=>`[${q.category}]\n${q.question}\n`).join("\n"); navigator.clipboard.writeText(t).then(()=>toast("Copied!","success")); }}
                  style={{ background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#334155" }}>📋 Copy All</button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="filter-bar" style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
        <button onClick={() => nav("/jobs")} style={btnStyle("#e2e8f0", "#334155")}>← Jobs</button>
        <h1 style={{ margin: 0, color: "#1e3a8a", fontSize: 22 }} lang={localeForLanguage(job.language)}>{job.title}</h1>
        <span style={{
          background: job.status === "active" ? "#dcfce7" : "#fef9c3",
          color: job.status === "active" ? "#16a34a" : "#854d0e",
          padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600
        }}>{job.status}</span>
        <span style={{
          background: "#eef2ff", color: "#4338ca", border: "1px solid #e0e7ff",
          padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600
        }}>{job.language || "English"}</span>
        <button onClick={startEdit} style={btnStyle("#f1f5f9", "#334155")}>✏️ Edit</button>
        {job.status === "active" && (
          <button
            onClick={() => deactivateMutation.mutate()}
            disabled={deactivateMutation.isPending}
            style={{ ...btnStyle("#fef9c3", "#854d0e"), border: "1px solid #fde68a", opacity: deactivateMutation.isPending ? 0.6 : 1 }}
          >{deactivateMutation.isPending ? "Deactivating…" : "⏸ Deactivate"}</button>
        )}
        <button
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
          style={{ ...btnStyle("#fee2e2", "#dc2626"), marginLeft: "auto", opacity: deleteMutation.isPending ? 0.6 : 1 }}
        >{deleteMutation.isPending ? "Deleting..." : "🗑 Delete"}</button>
        {job.status === "active" && branding?.career_page_url && (
          <CopyCareerLinkBtn careerPageUrl={branding.career_page_url} jobId={Number(id)} />
        )}
      </div>

      <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* JD Card */}
        <div style={cardStyle}>
          <h3 style={sectionHead}>Job Details</h3>
          {jd.summary && <p style={{ fontSize: 13, color: "#334155", lineHeight: 1.6 }}>{jd.summary}</p>}
          <div style={{ marginTop: 12 }}>
            <strong style={{ fontSize: 12, color: "#1e3a8a" }}>Required Skills</strong>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
              {jd.required_skills?.map((s: string) => (
                <span key={s} style={pill("#eff6ff", "#1d4ed8")}>{s}</span>
              ))}
            </div>
          </div>
          {jd.experience_years && (
            <div style={{ marginTop: 10, fontSize: 13, color: "#64748b" }}>
              Experience: <strong>{jd.experience_years}</strong>
            </div>
          )}

          {job.status === "draft" && (
            <button
              onClick={() => activateMutation.mutate()}
              disabled={activateMutation.isPending}
              style={{ ...btnStyle("linear-gradient(135deg,#1e3a8a,#00c9a7)", "white"), marginTop: 16, opacity: activateMutation.isPending ? 0.6 : 1 }}
            >{activateMutation.isPending ? "Activating…" : "Activate Job"}</button>
          )}
        </div>

        {/* Social Sharing Card */}
        <div style={cardStyle}>
          <h3 style={sectionHead}>Social Sharing</h3>
          <p style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
            Posts the text below as a LinkedIn update.
            Connect your account in <strong>Settings → Social Media</strong>.
          </p>

          {/* Post text preview */}
          {job.linkedin_post_text && (
            <div style={{
              background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6,
              padding: 12, fontSize: 12, color: "#334155",
              whiteSpace: "pre-wrap", maxHeight: 120, overflow: "auto",
              lineHeight: 1.5, marginBottom: 14,
            }}>{job.linkedin_post_text}</div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

            {/* LinkedIn */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ width: 120, fontSize: 12, color: "#64748b", display: "flex", alignItems: "center", gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#0a66c2"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/></svg>
                LinkedIn
              </div>
              {liConnected ? (
                <button onClick={() => linkedInMutation.mutate()} disabled={linkedInMutation.isPending}
                  style={{ ...btnStyle("#0a66c2", "white"), fontSize: 12, padding: "6px 14px", opacity: linkedInMutation.isPending ? 0.7 : 1 }}>
                  {linkedInMutation.isPending ? "Posting…" : job.linkedin_posted_at ? "Repost" : "Post to LinkedIn"}
                </button>
              ) : (
                (liAuthData as any)?.auth_url
                  ? <a href={(liAuthData as any).auth_url} style={{ fontSize: 12, color: "#0a66c2", fontWeight: 600 }}>Connect →</a>
                  : <span style={{ fontSize: 12, color: "#94a3b8" }}>Not configured</span>
              )}
              {job.linkedin_posted_at && <span style={{ fontSize: 11, color: "#16a34a" }}>✓ {formatDate(job.linkedin_posted_at, "short", tz)}</span>}
              {linkedInMsg && <span style={{ fontSize: 11, color: linkedInMsg.startsWith("✓") ? "#16a34a" : "#dc2626" }}>{linkedInMsg}</span>}
            </div>

          </div>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <JobScreeningQuestions jobId={Number(id)} />
      </div>

      {/* ── Kanban Pipeline ── */}
      <div style={cardStyle}>
        <input ref={cvInputRef} type="file" accept=".pdf,.doc,.docx" multiple style={{ display: "none" }} onChange={handleCVUpload} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
          <div>
            <h3 style={{ ...sectionHead, margin: 0, borderBottom: "none", paddingBottom: 0 }}>
              Hiring Pipeline
            </h3>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
              {pipelineApplications.length} of {job.applications?.length || 0} candidate{(job.applications?.length || 0) !== 1 ? "s" : ""} · drag cards to move stages
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {uploadProgress && (
              <span style={{ fontSize: 12, color: "#1d4ed8" }}>
                Uploading {uploadProgress.total} CV{uploadProgress.total !== 1 ? "s" : ""}…
              </span>
            )}
            {!uploadProgress && uploadMsg && (
              <span style={{ fontSize: 12, color: uploadMsg.startsWith("✓") ? "#16a34a" : "#dc2626" }}>{uploadMsg}</span>
            )}
            <button onClick={() => cvInputRef.current?.click()} disabled={uploading}
              style={{ background: "linear-gradient(135deg,#1e3a8a,#00c9a7)", color: "white", border: "none", borderRadius: 7, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: uploading ? "not-allowed" : "pointer", opacity: uploading ? 0.7 : 1 }}>
              {uploading ? `Uploading…` : "📤 Upload CVs"}
            </button>
          </div>
        </div>

        {(job.applications?.length || 0) === 0 ? (
          <div
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              const files = (Array.from(e.dataTransfer.files) as File[]).filter((f: File) => /\.(pdf|docx?)$/i.test(f.name));
              if (!files.length) return;
              const dt = new DataTransfer();
              files.forEach(f => dt.items.add(f));
              if (cvInputRef.current) {
                Object.defineProperty(cvInputRef.current, "files", { value: dt.files, writable: true });
                handleCVUpload({ target: cvInputRef.current } as any);
              }
            }}
            style={{ textAlign: "center", padding: "48px 0", color: "#94a3b8", border: "2px dashed #e2e8f0", borderRadius: 10, cursor: "pointer", transition: "border-color 0.15s" }}
            onClick={() => cvInputRef.current?.click()}
          >
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ marginBottom: 10 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#64748b" }}>Drop CVs here or click to upload</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>PDF or DOCX · multiple files supported{jobsEmail ? ` · or send to ${jobsEmail}` : ""}</div>
          </div>
        ) : (
          <>
            <ScreeningFilterBar jobId={Number(id)} onVisibleApplicationIdsChange={setVisibleAppIds} />
            <KanbanBoard
              applications={pipelineApplications}
              onStatusChange={(appId, newStatus) => statusMutation.mutate({ appId, status: newStatus })}
              onDownloadCV={handleDownloadCV}
              onDownloadDocx={handleDownloadDocx}
              onInterviewQs={handleGenerateIQ}
              onRegenerateCV={handleRegenerateCV}
              isMobile={isMobile}
              onViewProfile={(candidateId) => nav(`/candidates?highlight=${candidateId}`, { state: { returnTo: `/jobs/${id}` } })}
            />
          </>
        )}
      </div>

      {/* ── Talent Pool ── */}
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <h3 style={{ ...sectionHead, margin: 0, borderBottom: "none", paddingBottom: 0 }}>Smart Talent Pool</h3>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>Find past candidates from other roles who match this opening</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={handleTalentPool} disabled={tpLoading || !!talentPool}
              style={{ background: "linear-gradient(135deg,#1e3a8a,#00c9a7)", color: "white", border: "none", borderRadius: 7, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: (tpLoading || talentPool) ? "not-allowed" : "pointer", opacity: (tpLoading || talentPool) ? 0.5 : 1 }}>
              {tpLoading ? "Searching…" : "🔍 Find Matches"}
            </button>
            {talentPool && (
              <button onClick={() => setTalentPool(null)} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 7, padding: "7px 12px", fontSize: 13, color: "#64748b", cursor: "pointer", fontWeight: 500 }}>✕ Clear</button>
            )}
          </div>
        </div>

        {talentPool && (
          <>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
              Evaluated {talentPool.candidates_evaluated} of {talentPool.total_pool} candidates in your talent pool
              {talentPool.matches.length > 0 ? ` — ${talentPool.matches.length} match${talentPool.matches.length !== 1 ? "es" : ""} found` : " — no strong matches found"}
            </div>
            {talentPool.matches.length === 0 && (
              <div style={{ textAlign: "center", padding: "24px 0", color: "#94a3b8", fontSize: 13 }}>
                No candidates with compatibility ≥ 40% found in your talent pool.
              </div>
            )}
            {talentPool.matches.map((m: any) => (
              <div key={m.candidate_id} style={{ display: "flex", gap: 14, padding: "14px 16px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 8, alignItems: "flex-start" }}>
                <div style={{ textAlign: "center", minWidth: 52 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: m.compatibility_score >= 70 ? "#16a34a" : m.compatibility_score >= 50 ? "#d97706" : "#64748b" }}>
                    {m.compatibility_score}%
                  </div>
                  <div style={{ fontSize: 10, color: "#94a3b8" }}>match</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>{m.candidate_name}</div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {m.current_role || "—"}{m.experience_years ? ` · ${m.experience_years} yrs` : ""}
                    {m.previous_job ? <span style={{ marginLeft: 8, background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 4, padding: "1px 6px", fontSize: 11 }}>prev: {m.previous_job}</span> : ""}
                  </div>
                  <div style={{ fontSize: 12, color: "#16a34a", marginTop: 5 }}>✓ {m.match_reason}</div>
                  {m.potential_concern && <div style={{ fontSize: 12, color: "#d97706", marginTop: 3 }}>⚠ {m.potential_concern}</div>}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0, flexDirection: "column", alignItems: "flex-end" }}>
                  <button onClick={() => handleOutreachEmail(m)} style={btnStyle("#eff6ff", "#1d4ed8")}>✉ Draft Email</button>
                  {addedIds.has(m.candidate_id) ? (
                    <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>✓ In Pipeline</span>
                  ) : (
                    <button onClick={() => handleAddToPipeline(m)} disabled={addingId === m.candidate_id}
                      style={{ ...btnStyle("#f0fdf4", "#166534"), border: "1px solid #86efac", opacity: addingId === m.candidate_id ? 0.6 : 1 }}>
                      {addingId === m.candidate_id ? "Adding…" : "➕ Add to Pipeline"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
  padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
};
const sectionHead: React.CSSProperties = {
  color: "#1e3a8a", fontSize: 15, margin: "0 0 12px",
  borderBottom: "2px solid #00c9a7", paddingBottom: 6
};
function btnStyle(bg: string, color: string): React.CSSProperties {
  return { background: bg, color, border: "none", padding: "7px 14px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };
}
const labelStyle: React.CSSProperties = {
  display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 12px", borderRadius: 6,
  border: "1px solid #d1d5db", fontSize: 14, color: "#334155",
  outline: "none", boxSizing: "border-box"
};
function pill(bg: string, color: string): React.CSSProperties {
  return { background: bg, color, padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 500 };
}

// ── Copy Career Page Link ─────────────────────────────────────────────────

function CopyCareerLinkBtn({ careerPageUrl, jobId }: { careerPageUrl: string; jobId: number }) {
  const [copied, setCopied] = React.useState(false);
  const { toast } = useToast();

  async function copy() {
    navigator.clipboard.writeText(careerPageUrl).then(() => {
      setCopied(true);
      toast("Career page link copied!", "success");
      setTimeout(() => setCopied(false), 3000);
    });
  }

  return (
    <button onClick={copy}
      style={{ ...btnStyle(copied ? "#dcfce7" : "#f0fdf4", copied ? "#166534" : "#16a34a"), border: `1px solid ${copied ? "#86efac" : "#bbf7d0"}` }}>
      {copied ? "✓ Copied!" : "🔗 Career Page"}
    </button>
  );
}

// ── Kanban Board ──────────────────────────────────────────────────────────

const KANBAN_COLS = [
  { status: "new",          label: "New",          color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
  { status: "reviewed",     label: "Reviewed",     color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  { status: "shortlisted",  label: "Shortlisted",  color: "#16a34a", bg: "#f0fdf4", border: "#86efac" },
  { status: "screened_out", label: "Screened Out", color: "#92400e", bg: "#fffbeb", border: "#fde68a" },
  { status: "hired",        label: "Hired",        color: "#14532d", bg: "#dcfce7", border: "#4ade80" },
  { status: "rejected",     label: "Rejected",     color: "#991b1b", bg: "#fee2e2", border: "#fca5a5" },
];

const REC_COLORS_K: Record<string, string> = {
  "Strong Yes": "#16a34a", "Yes": "#2563eb", "Maybe": "#d97706", "No": "#dc2626",
};

function KanbanBoard({ applications, onStatusChange, onDownloadCV, onDownloadDocx, onInterviewQs, onRegenerateCV, isMobile, onViewProfile }: {
  applications: any[];
  onStatusChange: (appId: number, status: string) => void;
  onDownloadCV: (candidateId: number, applicationId?: number | null) => void;
  onDownloadDocx: (candidateId: number, applicationId?: number | null) => void;
  onInterviewQs: (app: any) => void;
  onRegenerateCV: (candidateId: number, applicationId?: number | null) => void;
  isMobile?: boolean;
  onViewProfile: (candidateId: number) => void;
}) {
  const tz = useTenantTimezone();
  const [dragging, setDragging] = React.useState<number | null>(null);
  const [dragOver, setDragOver] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<any | null>(null);
  const [mobileCol, setMobileCol] = React.useState("new");

  const grouped: Record<string, any[]> = {};
  KANBAN_COLS.forEach(c => { grouped[c.status] = []; });
  applications.forEach(app => {
    const col = app.status || "new";
    if (grouped[col]) grouped[col].push(app);
    else grouped["new"].push(app);
  });

  return (
    <>
      {/* Card detail modal */}
      {selected && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setSelected(null)}>
          <div style={{ background: "white", borderRadius: 12, padding: 24, maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>{selected.candidate_name}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  Applied {formatDate(selected.applied_at, "short", tz)}
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
              {selected.score != null && (
                <span style={{ background: selected.score >= 70 ? "#dcfce7" : selected.score >= 50 ? "#fef9c3" : "#fee2e2", color: selected.score >= 70 ? "#16a34a" : selected.score >= 50 ? "#d97706" : "#dc2626", padding: "3px 12px", borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
                  {selected.score}/100
                </span>
              )}
              {selected.recommendation && (
                <span style={{ background: (REC_COLORS_K[selected.recommendation] || "#64748b") + "18", color: REC_COLORS_K[selected.recommendation] || "#64748b", padding: "3px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                  {selected.recommendation}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Move to</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
              {KANBAN_COLS.filter(c => c.status !== selected.status).map(c => (
                <button key={c.status} onClick={() => { onStatusChange(selected.id, c.status); setSelected(null); }}
                  style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  {c.label}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {selected.has_formatted_cv && (
                <>
                  <button onClick={() => { onDownloadCV(selected.candidate_id, selected.application_id); setSelected(null); }} style={{ ...btnStyle("#0f766e", "white"), fontSize: 12 }}>⬇ PDF CV</button>
                  <button onClick={() => { onDownloadDocx(selected.candidate_id, selected.application_id); setSelected(null); }} style={{ ...btnStyle("#1d4ed8", "white"), fontSize: 12 }}>⬇ Word</button>
                </>
              )}
              <button onClick={() => { onRegenerateCV(selected.candidate_id, selected.application_id); setSelected(null); }} style={{ ...btnStyle("#475569", "white"), fontSize: 12 }}>🔄 Regenerate CV</button>
              <button onClick={() => { onInterviewQs(selected); setSelected(null); }} style={{ ...btnStyle("#7c3aed", "white"), fontSize: 12 }}>🎯 Interview Qs</button>
            </div>
            <div style={{ marginTop: 12, borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
              <button onClick={() => { onViewProfile(selected.candidate_id); setSelected(null); }}
                style={{ width: "100%", padding: "8px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, color: "#1e3a8a", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                View Full Profile →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: stage tabs */}
      {isMobile && (
        <div style={{ display: "flex", gap: 4, overflowX: "auto", paddingBottom: 10, marginBottom: 4 }}>
          {KANBAN_COLS.map(col => {
            const cnt = (grouped[col.status] || []).length;
            const active = mobileCol === col.status;
            return (
              <button key={col.status} onClick={() => setMobileCol(col.status)}
                style={{ flexShrink: 0, padding: "6px 14px", borderRadius: 20, border: `1px solid ${active ? col.border : "#e2e8f0"}`, background: active ? col.bg : "white", color: active ? col.color : "#64748b", fontWeight: active ? 700 : 500, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
                {col.label} {cnt > 0 && <span style={{ fontWeight: 700 }}>({cnt})</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Board — desktop: all columns | mobile: active column only */}
      <div style={{ display: "flex", gap: 10, overflowX: isMobile ? "visible" : "auto", paddingBottom: 8, minHeight: 300 }}>
        {KANBAN_COLS.map(col => {
          const cards = grouped[col.status] || [];
          const isOver = dragOver === col.status;
          if (isMobile && col.status !== mobileCol) return null;
          return (
            <div key={col.status}
              onDragOver={e => { e.preventDefault(); setDragOver(col.status); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={() => {
                if (dragging !== null && dragOver && dragOver !== applications.find(a => a.id === dragging)?.status) {
                  onStatusChange(dragging, col.status);
                }
                setDragging(null); setDragOver(null);
              }}
              style={{
                minWidth: isMobile ? "100%" : 180, flex: isMobile ? "1 1 100%" : "1 1 180px",
                background: isOver ? col.bg : "#f8fafc",
                border: `2px dashed ${isOver ? col.border : "#e2e8f0"}`,
                borderRadius: 10, padding: "10px 8px",
                transition: "background 0.15s, border-color 0.15s",
              }}>
              {/* Column header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, padding: "0 4px" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: col.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>{col.label}</span>
                <span style={{ fontSize: 11, background: col.bg, color: col.color, border: `1px solid ${col.border}`, borderRadius: 20, padding: "1px 8px", fontWeight: 700 }}>{cards.length}</span>
              </div>

              {/* Cards — fixed-height scrollable column */}
              <div style={{ maxHeight: 480, overflowY: "auto", paddingRight: 2 }}>
              {cards.map(app => {
                const scoreColor = app.score >= 70 ? "#16a34a" : app.score >= 50 ? "#d97706" : app.score > 0 ? "#dc2626" : "#94a3b8";
                const scoreBg    = app.score >= 70 ? "#dcfce7" : app.score >= 50 ? "#fef9c3" : app.score > 0 ? "#fee2e2" : "#f1f5f9";
                const initials   = (app.candidate_name || "?").split(" ").slice(0,2).map((w: string) => w[0]?.toUpperCase()).join("");
                const avatarColors = ["#1e3a8a","#0f766e","#7c3aed","#b45309","#1d4ed8","#dc2626"];
                const avatarBg = avatarColors[(app.candidate_name?.charCodeAt(0) || 0) % avatarColors.length];

                return (
                  <div key={app.id}
                    draggable
                    onDragStart={() => setDragging(app.id)}
                    onDragEnd={() => { setDragging(null); setDragOver(null); }}
                    onClick={() => setSelected(app)}
                    style={{
                      background: "white", borderRadius: 8, padding: "10px 12px",
                      marginBottom: 8, border: "1px solid #e2e8f0",
                      cursor: "grab", userSelect: "none",
                      boxShadow: dragging === app.id ? "0 4px 16px rgba(0,0,0,0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
                      opacity: dragging === app.id ? 0.5 : 1,
                      transition: "box-shadow 0.15s, opacity 0.15s",
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: avatarBg, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                        {initials}
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 12, color: "#0f172a", lineHeight: 1.3 }}>{app.candidate_name}</div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      {app.score != null ? (
                        <span style={{ background: scoreBg, color: scoreColor, padding: "1px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{app.score}</span>
                      ) : <span style={{ fontSize: 11, color: "#cbd5e1" }}>No score</span>}
                      {app.recommendation && (
                        <span style={{ fontSize: 10, color: REC_COLORS_K[app.recommendation] || "#94a3b8", fontWeight: 600 }}>{app.recommendation}</span>
                      )}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 5 }}>
                      <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                        {app.applied_at && (
                          <div style={{ fontSize: 10, color: "#94a3b8" }}>
                            {formatDate(app.applied_at, "compact", tz)}
                          </div>
                        )}
                        {app.notes_count > 0 && (
                          <span title={`${app.notes_count} note${app.notes_count !== 1 ? "s" : ""}`} style={{ fontSize: 9, background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", borderRadius: 8, padding: "1px 5px", fontWeight: 700 }}>💬 {app.notes_count}</span>
                        )}
                      </div>
                      {app.referred_by_name && (
                        <span title={`Referred by ${app.referred_by_name}`} style={{ fontSize: 9, background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 8, padding: "1px 5px", fontWeight: 600 }}>👥 {app.referred_by_name}</span>
                      )}
                    </div>
                  </div>
                );
              })}

              {cards.length === 0 && (
                <div style={{ textAlign: "center", padding: "20px 0", color: "#cbd5e1", fontSize: 12 }}>Drop here</div>
              )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
